import type { Point } from "features/dt-basic-setup/single-building/model/types";

import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { useContext, useEffect, useMemo, useState } from "react";

import { calculateOutlineAreaM2 } from "../lib/outlineArea";

export const useOutlineStep = () => {
  const {
    formData,
    outlineState: { isErrorOutline, isLoadingOutline },
    projectId,
    setBuildingOutline,
  } = useContext(SingleBuildingContext);

  const isReadOnly = !!projectId && formData.prefilledOutline;

  const outlinePoints = useMemo(() => formData.outline ?? [], [formData.outline]);

  const [isDrawing, setIsDrawing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const outlineAreaM2 = useMemo(() => calculateOutlineAreaM2(outlinePoints), [outlinePoints]);

  useEffect(() => {
    const handleEnter = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (!isDrawing) return;
      setIsDrawing(false);
    };
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [isDrawing]);

  const editableStrategy = useMemo(
    () => ({
      addPoint: (point: Point) => setBuildingOutline([...(formData.outline ?? []), point]),
      clearOutline: () => {
        setBuildingOutline([]);
        setIsDrawing(false);
        setIsEditing(false);
      },
      finishDrawing: () => setIsDrawing(false),
      isDrawing,
      isEditing,
      setIsDrawing: () => {
        setIsDrawing(true);
        setIsEditing(false);
      },
      setIsEditing: () => {
        setIsEditing(true);
        setIsDrawing(false);
      },
      updatePoint: (index: number, point: Point) =>
        setBuildingOutline((formData.outline ?? []).map((existingPoint, i) => (i === index ? point : existingPoint))),
    }),
    [formData.outline, isDrawing, isEditing, setBuildingOutline]
  );

  const readOnlyStrategy = useMemo(
    () => ({
      addPoint: (_point: Point) => {},
      clearOutline: () => {},
      finishDrawing: () => {},
      isDrawing: false,
      isEditing: false,
      setIsDrawing: () => {},
      setIsEditing: () => {},
      updatePoint: (_index: number, _point: Point) => {},
    }),
    []
  );

  const strategy = isReadOnly ? readOnlyStrategy : editableStrategy;

  return {
    addPoint: strategy.addPoint,
    buildingCenter: formData.buildingCenter,
    clearOutline: strategy.clearOutline,
    finishDrawing: strategy.finishDrawing,
    isDrawing: strategy.isDrawing,
    isEditing: strategy.isEditing,
    isErrorOutline,
    isLoadingOutline,
    isReadOnly,
    outlineAreaM2,
    outlinePoints,
    setIsDrawing: strategy.setIsDrawing,
    setIsEditing: strategy.setIsEditing,
    updatePoint: strategy.updatePoint,
  };
};
